import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Loader from '../Loader';

import { useSchemaPresetsQuery } from 'src/api/queries/schemesQueries';
import { usePlanLimitsQuery } from 'src/api/queries/subscriptionQueries';
import { useCreateSchemaFromPresetsMutation } from 'src/api/mutations/schemaMutations';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { SchemaPresetSummary } from 'src/types/TSchemes';

import styles from './SchemaPresetsModal.module.scss';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';

// Plan-gate 403s and structured 400s (e.g. the per-plan schema cap) carry an
// actionable message — surface it instead of the generic "Something went
// wrong". Returns null when the error carries nothing structured (call sites
// fall back to getErrorMessage).
function schemaErrorMessage(error: any): string | null {
  const gate = planUpgradeRequired(error);
  if (gate?.message) return gate.message;
  const body = error?.error;
  const errors = body && typeof body === 'object' && (body as any).data?.errors;
  if (Array.isArray(errors) && errors.length) return errors.join(', ');
  return null;
}


// Feature labels for display
const FEATURE_LABELS: Record<string, string> = {
  'approval-workflow': 'Approval Workflow',
  'rights-tracking': 'Rights Tracking',
  'expiry-management': 'Expiry Management',
  'venue-management': 'Venue Management',
  'capture-point-tracking': 'Capture Points',
  'guest-photo-lifecycle': 'Guest Photos',
  'partner-branding': 'Partner Branding',
  'multi-venue-operations': 'Multi-Venue',
};

// Format feature slug to human-readable label as fallback
const formatFeatureLabel = (feature: string): string =>
  feature
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Where "Add capacity" goes — the Subscription page honours `?purchase=` and
// opens that buy control on arrival, so the customer lands on the price rather
// than the top of a long page.
const SCHEMA_PURCHASE_ROUTE = '/admin/subscription?purchase=schemas';
const SUBSCRIPTION_ROUTE = '/admin/subscription';

type SchemaPresetsModalProps = BaseModalProps & {
  onSuccess?: () => void;
};

/**
 * The schema preset catalog — browsable on every plan, cap or no cap.
 *
 * It used to be an "Add Schema Presets" picker reachable only when you had a
 * free slot, which turned the schema cap into a wall: a customer at their limit
 * could not see what more capacity would give them, so "upgrade" was a decision
 * with no visible upside. Now the catalog always opens, always shows every
 * preset and the fields it brings, and at the cap swaps the checkboxes for the
 * route to the capacity needed to use them.
 *
 * It also filtered to `category === 'core'`, hiding most of the catalog for no
 * stated reason — the industry presets ARE the argument for buying capacity, so
 * they are all shown.
 */
export const SchemaPresetsModal: React.FC<SchemaPresetsModalProps> = ({
  onSuccess,
  ...modalProps
}) => {
  const { closeModal } = useModal();
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const history = useHistory();

  const [selectedPresets, setSelectedPresets] = useState<Set<string>>(new Set());

  const { data: presetsResult, isLoading: isLoadingPresets } = useSchemaPresetsQuery();
  const presets = useMemo<SchemaPresetSummary[]>(
    () => presetsResult?.data?.presets ?? [],
    [presetsResult],
  );

  // The same cap the page header shows, from the same endpoint, so the two can
  // never disagree. quota 0 == unlimited (project convention).
  const { data: limits } = usePlanLimitsQuery();
  const schemaLimit = limits?.asset_schemas;
  const quota = schemaLimit?.quota ?? 0;
  const used = schemaLimit?.used ?? 0;
  const unlimited = quota <= 0;
  const slotsFree = unlimited
    ? Number.POSITIVE_INFINITY
    : Math.max(0, quota - used - selectedPresets.size);
  const atCap = slotsFree <= 0;
  const canPurchase = (schemaLimit?.max_extra ?? 0) > 0;

  const togglePreset = (presetId: string) => {
    // Deselecting always works — that is how you free a slot for a different
    // preset. Selecting past the cap does not; the server refuses it anyway,
    // and reports the refusal inside a 201 that is easy to miss.
    if (!selectedPresets.has(presetId) && atCap) return;
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else {
        next.add(presetId);
      }
      return next;
    });
  };

  // "Select all" fills the slots available, in the order shown. Submitting
  // more than that just produces a 201 full of refusals.
  const selectAll = () => {
    const budget = unlimited ? presets.length : Math.max(0, quota - used);
    setSelectedPresets(new Set(presets.slice(0, budget).map((p) => p.preset_id)));
  };

  const clearAll = () => {
    setSelectedPresets(new Set());
  };

  const addPresetsMutation = useCreateSchemaFromPresetsMutation({
    onSuccess: (response) => {
      const { created, errors } = response.data;

      if (errors && errors.length > 0) {
        showAlert(`Added ${created} schema(s). Some failed: ${errors.join(', ')}`);
      } else {
        showSuccess(`Successfully added ${created} schema${created !== 1 ? 's' : ''}`);
      }

      closeModal();
      onSuccess?.();
    },
    onError: (error: any) => {
      showAlert(schemaErrorMessage(error) ?? getErrorMessage(error.message));
    },
  });

  const handleSubmit = () => {
    if (selectedPresets.size === 0) return;
    addPresetsMutation.mutate(Array.from(selectedPresets));
  };

  const goToCapacity = () => {
    closeModal();
    history.push(canPurchase ? SCHEMA_PURCHASE_ROUTE : SUBSCRIPTION_ROUTE);
  };

  const isLoading = addPresetsMutation.isLoading;
  const allSelected = presets.length > 0 && selectedPresets.size === presets.length;

  return (
    <BaseModal {...modalProps} containerStyle={{ maxWidth: '936px', width: '90vw' }}>
      <h2 className={styles.modalTitle}>Schema Presets</h2>
      <p className={styles.modalDescription}>
        Pre-built metadata schemas for common industries. Each one adds a set of
        fields your team can search, filter and bulk-edit on. Add one as-is, then
        edit it to fit how you actually work.
      </p>

      {/* The cap, stated once at the top with the action attached — so the
          answer to "why can't I tick these?" is on screen, with a route to the
          price, next to the thing being refused. */}
      {!unlimited && atCap && (
        <div className={styles.capNotice} data-test="schema-preset-cap-notice">
          <div className={styles.capNoticeText}>
            <strong>
              Your plan includes {quota} schema{quota === 1 ? '' : 's'}
              {used >= quota
                ? (quota === 1 ? ", and you're using it." : ", and you're using them all.")
                : ', and your selection fills it.'}
            </strong>{' '}
            Browse everything available below — adding any of these needs more capacity.
          </div>
          <Button mode="secondary" size="small" onClick={goToCapacity}>
            {canPurchase ? 'Add capacity' : 'Compare plans'}
          </Button>
        </div>
      )}

      {isLoadingPresets ? (
        <div className={styles.loadingWrapper}>
          <Loader />
        </div>
      ) : presets.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No schema presets available.</p>
        </div>
      ) : (
        <>
          <div className={styles.selectAllRow}>
            <button
              type="button"
              className={styles.selectAllButton}
              onClick={allSelected ? clearAll : selectAll}
              disabled={atCap && !allSelected}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
            <span className={styles.selectedCount}>
              {unlimited
                ? `${selectedPresets.size} of ${presets.length} selected`
                : `${selectedPresets.size} selected · ${slotsFree} schema${slotsFree === 1 ? '' : 's'} left on your plan`}
            </span>
          </div>

          <div className={styles.presetsContainer}>
            <div className={styles.presetsGrid}>
              {presets.map((preset) => {
                const isSelected = selectedPresets.has(preset.preset_id);
                // Locked, not hidden: being able to read it is the point.
                const isLocked = atCap && !isSelected;
                return (
                  <div
                    key={preset.preset_id}
                    className={[
                      styles.presetCard,
                      isSelected ? styles.selected : '',
                      isLocked ? styles.locked : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => togglePreset(preset.preset_id)}
                  >
                    <div className={styles.presetHeader}>
                      {isLocked ? (
                        <span className={styles.lockedChip}>Needs capacity</span>
                      ) : (
                        <Checkbox
                          checked={isSelected}
                          onChange={() => togglePreset(preset.preset_id)}
                        />
                      )}
                      <span className={styles.presetName}>
                        {preset.name?.en || preset.schema_title}
                      </span>
                    </div>
                    <p className={styles.presetDescription}>{preset.description}</p>
                    {/* What you actually get. Served by the presets endpoint so
                        it cannot drift from the schema JSON, and previewing what
                        this preset ADDS — every preset shares the same Title /
                        Description / Tags base, so listing that would make all
                        ten look identical. */}
                    {Boolean(preset.field_count) && (
                      <div className={styles.presetFields}>
                        <strong>{preset.field_count} fields</strong>
                        {preset.field_names && preset.field_names.length > 0 && (
                          <> — adds {preset.field_names.join(', ')}…</>
                        )}
                      </div>
                    )}
                    {preset.features && preset.features.length > 0 && (
                      <div className={styles.presetMeta}>
                        <div className={styles.featureBadges}>
                          {preset.features.map((feature) => (
                            <span key={feature} className={styles.featureBadge}>
                              {FEATURE_LABELS[feature] || formatFeatureLabel(feature)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className={styles.actions}>
        <Button mode="tertiary" size="small" onClick={closeModal}>
          {atCap && selectedPresets.size === 0 ? 'Close' : 'Cancel'}
        </Button>
        {atCap && selectedPresets.size === 0 ? (
          <Button mode="primary" size="small" onClick={goToCapacity}>
            {canPurchase ? 'Add capacity' : 'Compare plans'}
          </Button>
        ) : (
          <Button
            mode="primary"
            size="small"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={selectedPresets.size === 0 || isLoading}
          >
            Add {selectedPresets.size > 0 ? `${selectedPresets.size} ` : ''}Schema
            {selectedPresets.size !== 1 ? 's' : ''}
          </Button>
        )}
      </div>
    </BaseModal>
  );
};

export default SchemaPresetsModal;
